/**
 * CrashSeverityDetailView - Detail view for drilling into specific crash severity
 *
 * Features:
 * - Crash details by severity type (Fatal/Injury/Towaway)
 * - Geographic distribution of crashes
 * - Timeline trend for the severity
 * - Weather and road condition breakdown
 * - Vehicle configuration analysis
 * - Detailed crash table
 */

import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  AlertTriangle,
  MapPin,
  TrendingUp,
  Cloud,
  Sun,
  Truck,
  Calendar,
  Users,
} from 'lucide-react';
import { useDrillDown } from '../../contexts/DrillDownContext';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface CrashData {
  fatalities?: number;
  injuries?: number;
  report_state?: string;
  reportState?: string;  // CamelCase alias
  state?: string;
  tow_away?: string;
  towAway?: string;  // CamelCase alias
  report_date?: string;
  reportDate?: string;  // CamelCase alias
  crash_date?: string;
  crashDate?: string;  // CamelCase alias
  weather_condition?: string;
  road_condition?: string;
  light_condition?: string;
}

interface StateChartData {
  state: string;
  crashes: number;
  fatalities?: number;
  injuries?: number;
}

interface CrashSeverityDetailViewProps {
  data: {
    severity?: 'fatal' | 'injury' | 'towaway' | string;
    crashes?: CrashData[];
    month?: string;
    state?: string;
    totalCount?: number;
  };
}

// ============================================================================
// CONSTANTS
// ============================================================================

const CHART_COLORS = [
  '#23211d', '#b3412c', '#5d7148', '#b07d2b', '#6d685e',
  '#857f73', '#4a463f', '#7d561c', '#5d7148', '#4a463f',
];

const SEVERITY_COLORS = {
  fatal: '#b3412c',
  injury: '#b07d2b',
  towaway: '#23211d',
};

const SEVERITY_LABELS: Record<string, string> = {
  fatal: 'Fatal Crashes',
  injury: 'Injury Crashes',
  towaway: 'Tow-Away Crashes',
};

const WEATHER_ICONS: Record<string, string> = {
  'Clear': 'sun',
  'Rain': 'cloud-rain',
  'Snow': 'snowflake',
  'Fog': 'cloud-fog',
  'Cloudy': 'cloud',
};

// ============================================================================
// COMPONENT
// ============================================================================

const CrashSeverityDetailView: React.FC<CrashSeverityDetailViewProps> = ({ data }) => {
  const { drillDown } = useDrillDown();

  const severityType = data?.severity || 'unknown';
  const severityLabel = SEVERITY_LABELS[severityType] || 'Crashes';
  const crashes = data?.crashes || [];

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalCrashes = crashes.length;
    const totalFatalities = crashes.reduce((sum, c) => sum + (c.fatalities || 0), 0);
    const totalInjuries = crashes.reduce((sum, c) => sum + (c.injuries || 0), 0);
    const towawayCount = crashes.filter(c => c.towAway === 'Y').length;

    // Calculate average casualties per crash
    const avgFatalities = totalCrashes > 0 ? (totalFatalities / totalCrashes).toFixed(2) : '0';
    const avgInjuries = totalCrashes > 0 ? (totalInjuries / totalCrashes).toFixed(2) : '0';

    // Get unique states
    const states = new Set(crashes.map(c => c.report_state || c.state));

    return {
      totalCrashes,
      totalFatalities,
      totalInjuries,
      towawayCount,
      avgFatalities,
      avgInjuries,
      stateCount: states.size,
    };
  }, [crashes]);

  // Geographic distribution
  const geographicDistribution = useMemo(() => {
    const stateMap = new Map<string, { crashes: number; fatalities: number; injuries: number }>();

    crashes.forEach(c => {
      const state = c.report_state || c.state || 'Unknown';
      if (!stateMap.has(state)) {
        stateMap.set(state, { crashes: 0, fatalities: 0, injuries: 0 });
      }
      const stateData = stateMap.get(state)!;
      stateData.crashes++;
      stateData.fatalities += c.fatalities || 0;
      stateData.injuries += c.injuries || 0;
    });

    return Array.from(stateMap.entries())
      .map(([state, stats]) => ({ state, ...stats }))
      .sort((a, b) => b.crashes - a.crashes)
      .slice(0, 15);
  }, [crashes]);

  // Timeline trend
  const timelineTrend = useMemo(() => {
    const monthMap = new Map<string, { crashes: number; fatalities: number; injuries: number }>();

    crashes.forEach(c => {
      const dateStr = c.reportDate || c.crashDate;
      if (dateStr) {
        const date = new Date(dateStr);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!monthMap.has(key)) {
          monthMap.set(key, { crashes: 0, fatalities: 0, injuries: 0 });
        }
        const monthData = monthMap.get(key)!;
        monthData.crashes++;
        monthData.fatalities += c.fatalities || 0;
        monthData.injuries += c.injuries || 0;
      }
    });

    return Array.from(monthMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-12)
      .map(([month, stats]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        ...stats,
      }));
  }, [crashes]);

  // Weather condition breakdown
  const weatherBreakdown = useMemo(() => {
    const weatherMap = new Map<string, number>();

    crashes.forEach(c => {
      const weather = c.weather_condition || 'Unknown';
      weatherMap.set(weather, (weatherMap.get(weather) || 0) + 1);
    });

    return Array.from(weatherMap.entries())
      .map(([condition, count]) => ({ condition, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [crashes]);

  // Road condition breakdown
  const roadBreakdown = useMemo(() => {
    const roadMap = new Map<string, number>();

    crashes.forEach(c => {
      const road = c.road_condition || 'Unknown';
      roadMap.set(road, (roadMap.get(road) || 0) + 1);
    });

    return Array.from(roadMap.entries())
      .map(([condition, count]) => ({ condition, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [crashes]);

  // Light condition breakdown
  const lightBreakdown = useMemo(() => {
    const lightMap = new Map<string, number>();

    crashes.forEach(c => {
      const light = c.light_condition || 'Unknown';
      lightMap.set(light, (lightMap.get(light) || 0) + 1);
    });

    return Array.from(lightMap.entries())
      .map(([condition, count]) => ({ condition, count }))
      .sort((a, b) => b.count - a.count);
  }, [crashes]);

  // Day of week distribution
  const dayOfWeekDistribution = useMemo(() => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayMap = new Map<number, number>();

    for (let i = 0; i < 7; i++) {
      dayMap.set(i, 0);
    }

    crashes.forEach(c => {
      const dateStr = c.reportDate || c.crashDate;
      if (dateStr) {
        const date = new Date(dateStr);
        const dow = date.getDay();
        dayMap.set(dow, (dayMap.get(dow) || 0) + 1);
      }
    });

    return Array.from(dayMap.entries())
      .map(([day, count]) => ({ day: dayNames[day], count }));
  }, [crashes]);

  // Handle state click
  const handleStateClick = (state: string) => {
    const stateCrashes = crashes.filter(c => (c.report_state || c.state) === state);
    drillDown('state-detail', state, {
      state,
      crashes: stateCrashes,
      severity: severityType,
    });
  };

  // Get severity color
  const severityColor = SEVERITY_COLORS[severityType as keyof typeof SEVERITY_COLORS] || '#857f73';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Severity Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '1rem',
        backgroundColor: `${severityColor}15`,
        borderLeft: `4px solid ${severityColor}`,
        borderRadius: '8px',
      }}>
        <AlertTriangle size={28} style={{ color: severityColor }} />
        <div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0, color: 'var(--color-text)' }}>
            {severityLabel}
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', margin: 0 }}>
            {data?.month ? `Month: ${data.month}` : ''}
            {data?.state ? ` | State: ${data.state}` : ''}
            {!data?.month && !data?.state ? 'Crash Severity Analysis' : ''}
          </p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="drill-down-stats-row">
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value" style={{ color: severityColor }}>
            {summaryStats.totalCrashes.toLocaleString()}
          </div>
          <div className="drill-down-stat-label">Total Crashes</div>
        </div>
        {severityType === 'fatal' && (
          <div className="drill-down-stat-item">
            <div className="drill-down-stat-value" style={{ color: 'var(--color-danger)' }}>
              {summaryStats.totalFatalities.toLocaleString()}
            </div>
            <div className="drill-down-stat-label">Total Fatalities</div>
          </div>
        )}
        {severityType === 'injury' && (
          <div className="drill-down-stat-item">
            <div className="drill-down-stat-value" style={{ color: 'var(--color-warning)' }}>
              {summaryStats.totalInjuries.toLocaleString()}
            </div>
            <div className="drill-down-stat-label">Total Injuries</div>
          </div>
        )}
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value">{summaryStats.stateCount}</div>
          <div className="drill-down-stat-label">States Affected</div>
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value">
            {severityType === 'fatal' ? summaryStats.avgFatalities : summaryStats.avgInjuries}
          </div>
          <div className="drill-down-stat-label">
            Avg {severityType === 'fatal' ? 'Fatalities' : 'Injuries'}/Crash
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="drill-down-detail-grid">
        {/* Timeline Trend */}
        <div className="drill-down-detail-card" style={{ gridColumn: 'span 2' }}>
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <TrendingUp size={18} style={{ color: 'var(--color-primary)' }} />
              {severityLabel} Over Time
            </h4>
            <span className="drill-down-detail-card-badge">12 Months</span>
          </div>
          {timelineTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={timelineTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd1" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: '0.8125rem' }} />
                <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                <Area
                  type="monotone"
                  dataKey="crashes"
                  stroke={severityColor}
                  fill={severityColor}
                  fillOpacity={0.3}
                  name="Crashes"
                />
                {severityType === 'fatal' && (
                  <Line
                    type="monotone"
                    dataKey="fatalities"
                    stroke="#b3412c"
                    strokeWidth={2}
                    name="Fatalities"
                  />
                )}
                {severityType === 'injury' && (
                  <Line
                    type="monotone"
                    dataKey="injuries"
                    stroke="#b07d2b"
                    strokeWidth={2}
                    name="Injuries"
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No timeline data available
            </div>
          )}
        </div>

        {/* Geographic Distribution */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <MapPin size={18} style={{ color: 'var(--color-success)' }} />
              Geographic Distribution
            </h4>
            <span className="drill-down-detail-card-badge">Top 15 States</span>
          </div>
          {geographicDistribution.length > 0 ? (
            <div className="chart-clickable">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={geographicDistribution} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd1" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="state" tick={{ fontSize: 10 }} width={35} />
                  <Tooltip contentStyle={{ fontSize: '0.8125rem' }} />
                  <Bar
                    dataKey="crashes"
                    fill={severityColor}
                    name="Crashes"
                    onClick={(d) => handleStateClick((d as unknown as StateChartData).state)}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No geographic data available
            </div>
          )}
        </div>

        {/* Day of Week Distribution */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <Calendar size={18} style={{ color: 'var(--color-info)' }} />
              Day of Week Pattern
            </h4>
          </div>
          {dayOfWeekDistribution.some(d => d.count > 0) ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dayOfWeekDistribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd1" />
                <XAxis dataKey="day" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: '0.8125rem' }} />
                <Bar dataKey="count" fill="#6d685e" name="Crashes" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No day of week data available
            </div>
          )}
        </div>

        {/* Weather Conditions */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <Cloud size={18} style={{ color: 'var(--color-warning)' }} />
              Weather Conditions
            </h4>
          </div>
          {weatherBreakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={weatherBreakdown}
                  dataKey="count"
                  nameKey="condition"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={({ name, percent }) => `${((percent as number) * 100).toFixed(0)}%`}
                >
                  {weatherBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ fontSize: '0.8125rem' }}
                  formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No weather data available
            </div>
          )}
        </div>

        {/* Road Conditions */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <Truck size={18} style={{ color: 'var(--color-primary)' }} />
              Road Conditions
            </h4>
          </div>
          {roadBreakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={roadBreakdown} layout="vertical" margin={{ left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd1" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="condition"
                  tick={{ fontSize: 9 }}
                  width={55}
                />
                <Tooltip contentStyle={{ fontSize: '0.8125rem' }} />
                <Bar dataKey="count" fill="#5d7148" name="Crashes" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No road condition data available
            </div>
          )}
        </div>

        {/* Light Conditions */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <Sun size={18} style={{ color: '#b07d2b' }} />
              Light Conditions
            </h4>
          </div>
          {lightBreakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={lightBreakdown}
                  dataKey="count"
                  nameKey="condition"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={({ name, percent }) => `${((percent as number) * 100).toFixed(0)}%`}
                >
                  {lightBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ fontSize: '0.8125rem' }}
                  formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No light condition data available
            </div>
          )}
        </div>
      </div>

      {/* Crash Details Table */}
      {crashes.length > 0 && (
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <AlertTriangle size={18} style={{ color: severityColor }} />
              Crash Details
            </h4>
            <span className="drill-down-detail-card-badge">
              {Math.min(crashes.length, 50)} of {crashes.length}
            </span>
          </div>
          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border)', position: 'sticky', top: 0, backgroundColor: '#fbf9f4' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Date</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>State</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Fatalities</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Injuries</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Weather</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Road</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Light</th>
                </tr>
              </thead>
              <tbody>
                {crashes.slice(0, 50).map((c, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.75rem' }}>
                      {c.reportDate || c.crashDate
                        ? new Date(c.reportDate || c.crashDate).toLocaleDateString()
                        : '-'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <span
                        style={{ color: 'var(--color-primary)', cursor: 'pointer' }}
                        onClick={() => handleStateClick(c.reportState || c.state)}
                      >
                        {c.reportState || c.state || '-'}
                      </span>
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      textAlign: 'center',
                      color: (c.fatalities || 0) > 0 ? 'var(--color-danger)' : 'inherit',
                      fontWeight: (c.fatalities || 0) > 0 ? 600 : 400,
                    }}>
                      {c.fatalities || 0}
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      textAlign: 'center',
                      color: (c.injuries || 0) > 0 ? 'var(--color-warning)' : 'inherit',
                      fontWeight: (c.injuries || 0) > 0 ? 600 : 400,
                    }}>
                      {c.injuries || 0}
                    </td>
                    <td style={{ padding: '0.75rem' }}>{c.weather_condition || '-'}</td>
                    <td style={{ padding: '0.75rem' }}>{c.road_condition || '-'}</td>
                    <td style={{ padding: '0.75rem' }}>{c.light_condition || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrashSeverityDetailView;
